import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  NgZone,
  AfterViewInit,
  TemplateRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { Paginator } from 'primeng/paginator';
import { MessageService } from 'primeng/api';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  Subscription,
} from 'rxjs';
import { StorageService } from 'src/app/services/storage.service';
import { WalletService } from 'src/app/services/wallet.service';
import { OrderService } from 'src/app/services/order.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TableColumn } from 'src/app/shared-components/data-table/data-table.component';

interface Transaction {
  slNo: number;
  clientVendor: string;
  transactionId: string;
  orderId: string;
  dateTime: string;
  purpose: string;
  status: 'Success' | 'In-progress' | 'Failed';
  transactionType: 'Debit' | 'Credit';
  totalAmount: number;
}

@Component({
  selector: 'app-transaction-details',
  templateUrl: './transaction-details.component.html',
  styleUrls: ['./transaction-details.component.scss'],
  standalone: false,
})
export class TransactionDetailsComponent implements OnInit {
  readonly columns: TableColumn[] = [
    { field: 's_no', header: 'Sl. No' },
    { field: 'created_at', header: 'Date & Time' },
    { field: 'order_id', header: 'Order ID' },
    { field: 'transaction_id', header: 'Transaction ID' },
    { field: 'utr', header: 'UTR', width: '220px' },
    { field: 'additional_data.order_type', header: 'Order Type', fallback: '--' },
    { field: 'purpose', header: 'Purpose' },
    { field: 'status', header: 'Status' },
    { field: 'payment_mode', header: 'Payment Mode', fallback: '--' },
    { field: 'transaction_type', header: 'Transaction Type' },
    { field: 'amount', header: 'Total Amount' },
  ];

  @ViewChild('viewOrderData') viewOrderData: any;
  @ViewChild('paymentSummaryModal') paymentSummaryModal!: TemplateRef<any>;
  // Tab management
  activeTab = 'transactions';

  // Transactions data
  transactions: Transaction[] = [];
  filteredTransactions: Transaction[] = [];

  // Common pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalPages: number;

  // Transaction filters
  searchTerm = '';
  dateRange: Date[] = [];
  statusFilter: any = { label: 'Status: All', value: 'All' };
  typeFilter: any = { label: 'Type: All', value: 'All' };

  // Dropdown options
  statusOptions = [
    { label: 'Status: All', value: 'All' },
    { label: 'Success', value: 'Successful' },
    // { label: 'In-progress', value: 'Pending' },
    { label: 'Failed', value: 'Failed' },
  ];

  typeOptions = [
    { label: 'Type: All', value: 'All' },
    { label: 'Debit', value: 'Debit' },
    { label: 'Credit', value: 'Credit' },
  ];

  numberoftables: number;
  searchPageNumber: number = 1;
  totalRecords: any;
  pageNo: number = 1;
  rowsPerPage: number = 10;
  isTransactionsLoading: boolean;
  filteredData: any[] = [];
  customerName: string;
  currentBalance: string;
  virtualAccountNo: string;
  bankName: string;
  isWallet: boolean = false;
  viewOrderId: any;
  paymentAmountData: any;
  accountId: any;
  userRole: string;

  private searchSubject = new Subject<string>();
  private subscription = new Subscription();
  constructor(
    private router: Router,
    private walletService: WalletService,
    private storageService: StorageService,
    private messageService: MessageService,
    private orderService: OrderService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
  ) {
    this.virtualAccountNo = this.storageService.getVirtualAccountNumber();
    this.isWallet = this.storageService.getIsWallet();
    this.accountId = this.storageService.getAccountId();
    this.userRole = this.storageService.getRole() || null;
  }

  ngOnInit(): void {
    this.subscription = this.searchSubject
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe(() => {
        this.searchTerm = this.searchTerm.trim();
        this.currentPage = 1;
        this.loadTransactionsData();
      });
    this.loadTransactionHeaderDetails();
  }
  // =================== TRANSACTIONS Header Section ===================
  loadTransactionHeaderDetails() {
    this.walletService.getWalletClientDetails(this.virtualAccountNo).subscribe(
      (data) => {
        this.customerName = data.content.name;
        this.currentBalance = data.content.current_balance;
        this.virtualAccountNo = data.content.virtual_account_no;
        this.bankName = data.content.bank;
        this.loadTransactionsData();
      },
      (error) => {
        console.error('Error loading wallet details:', error);
      },
    );
  }

  // =================== TRANSACTIONS SECTION ===================

  loadTransactionsData() {
    this.isTransactionsLoading = true;
    this.virtualAccountNo = this.storageService.getVirtualAccountNumber();

    const params = {
      page: this.currentPage,
      page_size: this.itemsPerPage,
      search: this.searchTerm.trim(),
      status: this.statusFilter?.value !== 'All' ? this.statusFilter.value : '',
      transaction_type:
        this.typeFilter?.value !== 'All' ? this.typeFilter.value : '',
      from_date:
        this.dateRange && this.dateRange[0]
          ? this.formatDateForAPI(this.dateRange[0])
          : '',
      to_date:
        this.dateRange && this.dateRange[1]
          ? this.formatDateForAPI(this.dateRange[1])
          : '',
    };

    // Remove empty parameters
    Object.keys(params).forEach((key) => {
      if (
        params[key] === '' ||
        params[key] === null ||
        params[key] === undefined
      ) {
        delete params[key];
      }
    });

    this.walletService
      .getTransactionDetails(this.virtualAccountNo, params)
      .subscribe({
        next: (response) => {
          // Handle different response structures
          this.transactions = response.content?.data || response.content || [];
          this.totalRecords =
            response.content?.pagination?.total_items ||
            response.totalElements ||
            response.total ||
            this.transactions.length;
          this.numberoftables = Math.ceil(
            this.totalRecords / this.itemsPerPage,
          );
          this.searchPageNumber = this.currentPage;
          this.isTransactionsLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading transactions:', error);
          this.transactions = [];
          this.totalRecords = 0;
          this.numberoftables = 0;
          this.isTransactionsLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private formatDateForAPI(date: Date): string {
    // Use local date components instead of UTC conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`; // Returns YYYY-MM-DD format in local timezone
  }

  // =================== FILTER HANDLERS ===================
  onSearch() {
    this.searchSubject.next(this.searchTerm);
  }

  onDateRangeChange() {
    this.currentPage = 1;
    this.loadTransactionsData();
  }

  onStatusChange() {
    this.currentPage = 1;
    this.loadTransactionsData();
  }

  onTypeChange() {
    this.currentPage = 1;
    this.loadTransactionsData();
  }

  // =================== PAGINATION HANDLERS ===================
  onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.itemsPerPage = event.rows;
    this.loadTransactionsData();
  }

  pageChange(page: number) {
    if (page >= 1 && page <= this.numberoftables) {
      this.currentPage = page;
      this.loadTransactionsData();
    }
  }

  // =================== TRACKBY FUNCTIONS ===================
  trackByTransactionId(index: number, transaction: Transaction): any {
    return transaction.transactionId || index;
  }

  trackByCustomerId(index: number, customer: any): any {
    return customer.id || customer.account_number || index;
  }

  // =================== NAVIGATION METHODS ===================
  createWallet() {
    this.router.navigate(['doqfy/wallet/create-wallet']);
  }

  moneyTransfer() {
    this.router.navigate(['doqfy/wallet/money-transfer']);
  }

  exportData() {
    // Build export params with only the active filters
    const rawParams: Record<string, string> = {
      search: this.searchTerm.trim(),
      status: this.statusFilter?.value !== 'All' ? this.statusFilter.value : '',
      type: this.typeFilter?.value !== 'All' ? this.typeFilter.value : '',
      dateFrom:
        this.dateRange && this.dateRange[0]
          ? this.formatDateForAPI(this.dateRange[0])
          : '',
      dateTo:
        this.dateRange && this.dateRange[1]
          ? this.formatDateForAPI(this.dateRange[1])
          : '',
    };

    const exportParams = Object.fromEntries(
      Object.entries(rawParams).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );

    // Call your export API with current filters
    let fileName = 'wallet_report';
    this.walletService
      .exportTransactions(this.virtualAccountNo, exportParams)
      .subscribe((res) => {
        if (res.status === 200 && res['message'] != 'No records found') {
          this.walletService.base64ToCSV(res['content'], fileName);
          this.messageService.add({
            severity: 'success',
            summary: 'File downloaded successfully !',
            icon: 'pi-check-circle',
          });
        } else {
          this.messageService.add({ severity: 'warn', summary: res.message });
        }
      });
  }

  goBack() {
    this.router.navigate(['/doqfy/admin-dashboard']);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'successful':
      case 'completed':
        return 'status-success';
      case 'pending':
      case 'processing':
        return 'status-pending';
      case 'failed':
      case 'error':
        return 'status-failed';
      default:
        return 'status-pending';
    }
  }

  copyUtrToClipboard(text: string, transaction: any) {
    if (!navigator.clipboard) {
      console.error('Clipboard API not available');
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        transaction.justCopied = true;
        this.cdr.detectChanges();

        setTimeout(() => {
          transaction.justCopied = false;
          this.cdr.detectChanges();
        }, 1000);
      })
      .catch((err) => {
        transaction.justCopied = false;
        this.cdr.detectChanges();
        console.error('Could not copy UTR: ', err);
      });
  }

  navigateToOrder(transaction: any) {
    const orderId = transaction.additional_data.order_id;
    const description = transaction.additional_data.description;
    const isBulkOrder = transaction.additional_data.order_type === 'BULK';

    if (description === 'Pre-Procurement Payment') {
      this.router.navigate(['/doqfy/pre-procurement/procurement-view'], {
        queryParams: {
          orderId: orderId,
          totalAmount: transaction.amount,
          createdOn: transaction.created_at,
        },
      });
    } else if (isBulkOrder) {
      this.router.navigate(['/doqfy/orders/orders-list'], {
        queryParams: {
          bulkOrderid: orderId,
        },
      });
    } else {
      this.viewOrder(orderId, this.viewOrderData);
    }
  }

  getAmountSummary(transaction: any) {
    this.paymentAmountData = null;
    const orderId = transaction.additional_data.order_id;
    const is_pre_order =
      transaction.additional_data.description === 'Pre-Procurement Payment';
    const is_bulk = transaction.additional_data.order_type === 'BULK';

    const modalRef = this.modalService.open(this.paymentSummaryModal, {
      centered: true,
      size: 'md',
    });

    this.orderService
      .getPaymentAmountDetails(orderId, is_pre_order, is_bulk)
      .subscribe({
        next: (res) => {
          this.paymentAmountData = res['content'];
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error fetching payment details:', error);
          modalRef.close();
        },
      });
  }

  viewOrder(order_id, content) {
    this.viewOrderId = { orderId: order_id, bulkOrderid: null };
    this.modalService
      .open(content, {
        size: 'xl',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.then(
        (result) => {
          console.log(result);
        },
        (reason) => {
          console.log(reason);
          // this.getOrderDetail(order_id, content);
        },
      );
  }

  // getOrderDetail(orderId, content) {
  //   this.orderService.getOrderDetail(orderId).subscribe((res: any) => {
  //     let orderDetails = res['content'][0];
  //   });
  // }
}
